import { Box3 } from 'three/webgpu'
import ClawPrize from './ClawPrize'

export default class ClawPrizes {
	static defaults = {
		prize: 'Cube113',
		count: 18,
		width: 26,
		tilt: 0.6,
		depth: 8,
		gap: 0.05,
		settle: 400,
		gravity: 80,
		step: { min: 1 / 240, max: 1 / 30 },
	}

	constructor(model, body, RAPIER) {
		this.options = ClawPrizes.defaults
		this.model = model
		this.RAPIER = RAPIER
		this.holding = false
		this.stop = this.options.depth
		this.world = new RAPIER.World({ x: 0, y: -this.options.gravity, z: 0 })
		this.#prizes(body)
		this.#stack()
		this.#bounds()
		this.#settle()
		this.rest = this.items[0].gripPoint().y
	}

	#prizes(body) {
		const { prize, count } = this.options
		const device = this.model.getObjectByName(prize)
		const devices = [device]

		for (let i = 1; i < count; i++) {
			const copy = device.clone()

			device.parent.add(copy)
			devices.push(copy)
		}

		this.items = devices.map(item => new ClawPrize(item, body, this.model, this.RAPIER, this.world, this.options.depth))
	}

	#stack() {
		const { width, tilt, gap } = this.options

		for (let i = 0; i < this.items.length; i++) {
			const prize = this.items[i]
			const x = (Math.random() - 0.5) * (width - prize.width * 2)
			const angle = (Math.random() - 0.5) * tilt

			prize.move(x, i * (prize.height * 1.5 + gap), angle)
		}
	}

	#settle() {
		this.world.timestep = 1 / 60

		for (let i = 0; i < this.options.settle; i++) this.world.step()
		for (let i = 0; i < this.items.length; i++) this.items[i].sync()
	}

	#bounds() {
		const { RAPIER } = this
		const box = new Box3()
		const edge = this.options.width / 2
		const ground = this.world.createRigidBody(RAPIER.RigidBodyDesc.fixed())
		let floor = Infinity

		for (const prize of this.items) floor = Math.min(floor, box.setFromObject(prize.device).min.y)

		const solid = (hx, hy, x, y) =>
			this.world.createCollider(RAPIER.ColliderDesc.cuboid(hx, hy, 50).setTranslation(x, y, 0).setFriction(0), ground)
		const wall = x => solid(1, 100, x, floor + 99)

		solid(1000, 50, 0, floor - 50)
		this.floor = floor
		this.edge = edge
		this.walls = [wall(-edge - 1), wall(edge + 1)]
	}

	set visible(visible) {
		for (let i = 0; i < this.items.length; i++) this.items[i].device.visible = visible
	}

	fit(edge) {
		if (Math.abs(edge - this.edge) < 0.01) return

		this.edge = edge
		this.walls[0].setTranslationWrtParent({ x: -edge - 1, y: this.floor + 99, z: 0 })
		this.walls[1].setTranslationWrtParent({ x: edge + 1, y: this.floor + 99, z: 0 })
	}

	#aim() {
		const grip = this.items[0].gripPoint().x
		let aim = null

		for (let i = 0; i < this.items.length; i++) {
			const prize = this.items[i]

			if (prize.held || Math.abs(prize.x - grip) > prize.width) continue
			if (!aim || prize.y > aim.y) aim = prize
		}

		return aim
	}

	#nearest() {
		let nearest = null

		for (let i = 0; i < this.items.length; i++) {
			const prize = this.items[i]

			if (!nearest || prize.distance < nearest.distance) nearest = prize
		}

		return nearest
	}

	update(delta, closed, grabbing) {
		const { min, max } = this.options.step
		const aim = grabbing && !this.holding ? this.#aim() : null
		const target = closed && !this.holding ? this.#nearest() : null
		let holding = false

		if (!grabbing) this.stop = this.options.depth
		else if (aim) this.stop = Math.max(0, this.rest - aim.y)

		for (let i = 0; i < this.items.length; i++) {
			const prize = this.items[i]

			prize.update(delta, closed && (prize.held || prize === target))
			holding ||= prize.held
		}

		this.holding = holding
		this.world.timestep = Math.min(max, Math.max(min, delta))
		this.world.step()

		for (let i = 0; i < this.items.length; i++) this.items[i].sync()
	}

	dispose() {
		this.world.free()
	}
}
